import type {
  ContactKindFilter,
  EnrichmentStatus,
  MissingField,
  PublicationStatus,
  SyncedLocationListParams,
  SyncedLocationSortField,
} from '@/types/api';

/**
 * The filter state of the synced-locations page lives in the URL, so a
 * filtered view can be shared, bookmarked and returned to with the browser's
 * back button. This module is the two directions of that: query string to
 * params, and params to query string (the latter shared with the API client,
 * because the page's URL and the API's query string are the same language).
 */

export const ENRICHMENT_STATUSES: EnrichmentStatus[] = ['pending', 'enriched'];
export const PUBLICATION_STATUSES: PublicationStatus[] = [
  'not_published', 'pending_review', 'published', 'rejected', 'unpublished',
];
export const MISSING_FIELDS: MissingField[] = [
  'location_type', 'bedrooms', 'bathrooms', 'description', 'images', 'city', 'country',
];
export const CONTACT_KINDS: ContactKindFilter[] = ['email', 'phone', 'website', 'social', 'none'];
export const SORT_FIELDS: SyncedLocationSortField[] = [
  'createdAt', 'updatedAt', 'lastSyncedAt', 'name', 'imageCount', 'accommodates', 'city', 'rating', 'price',
];

export const MISSING_FIELD_LABELS: Record<MissingField, string> = {
  location_type: 'Location type',
  bedrooms: 'Bedrooms',
  bathrooms: 'Bathrooms',
  description: 'Description',
  images: 'Images',
  city: 'City',
  country: 'Country',
};

export const CONTACT_KIND_LABELS: Record<ContactKindFilter, string> = {
  email: 'Email',
  phone: 'Phone',
  website: 'Website',
  social: 'Social profile',
  none: 'No contact',
};

export const SORT_LABELS: Record<SyncedLocationSortField, string> = {
  createdAt: 'Imported',
  updatedAt: 'Updated',
  lastSyncedAt: 'Last synced',
  name: 'Name',
  imageCount: 'Photos',
  accommodates: 'Capacity',
  city: 'City',
  rating: 'Rating',
  price: 'Price',
};

/** The taxonomy lists may hold a comma inside a value, so they travel `||`-separated. */
const PIPE_SEPARATED = new Set(['category', 'tag', 'amenity']);

function readList(sp: URLSearchParams, key: string): string[] | undefined {
  const raw = sp.get(key);
  if (raw === null || raw.trim() === '') return undefined;
  // A taxonomy value holds commas ("Lakes, Dams & Reservoirs"); a lone one has no `||` to find.
  const parts = raw.includes('||') || PIPE_SEPARATED.has(key) ? raw.split('||') : raw.split(',');
  const out = parts.map((p) => p.trim()).filter((p) => p !== '');
  return out.length > 0 ? out : undefined;
}

function readEnum<T extends string>(sp: URLSearchParams, key: string, allowed: readonly T[]): T[] | undefined {
  const values = readList(sp, key)?.filter((v): v is T => (allowed as readonly string[]).includes(v));
  return values && values.length > 0 ? values : undefined;
}

function readOne<T extends string>(sp: URLSearchParams, key: string, allowed: readonly T[]): T | undefined {
  const v = sp.get(key);
  return v !== null && (allowed as readonly string[]).includes(v) ? (v as T) : undefined;
}

function readBool(sp: URLSearchParams, key: string): boolean | undefined {
  const v = sp.get(key);
  return v === 'true' ? true : v === 'false' ? false : undefined;
}

function readNumber(sp: URLSearchParams, key: string): number | undefined {
  const v = sp.get(key);
  if (v === null || v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function readText(sp: URLSearchParams, key: string): string | undefined {
  const v = sp.get(key);
  return v !== null && v.trim() !== '' ? v : undefined;
}

/** The params a URL describes. Unknown keys and malformed values are ignored. */
export function paramsFromSearch(sp: URLSearchParams): SyncedLocationListParams {
  const p: SyncedLocationListParams = {};
  const set = <K extends keyof SyncedLocationListParams>(key: K, value: SyncedLocationListParams[K]): void => {
    if (value !== undefined) p[key] = value;
  };
  set('search', readText(sp, 'search'));
  set('partnerAccountIds', readList(sp, 'partnerAccountId'));
  set('sources', readList(sp, 'source'));
  set('assetClasses', readList(sp, 'assetClass'));
  set('countries', readList(sp, 'country'));
  set('cities', readList(sp, 'city'));
  set('enrichmentStatus', readOne(sp, 'enrichmentStatus', ENRICHMENT_STATUSES));
  set('publicationStatus', readOne(sp, 'publicationStatus', PUBLICATION_STATUSES));
  set('isBlocked', readBool(sp, 'isBlocked'));
  set('locationTypeIds', readList(sp, 'locationTypeId'));
  set('typeSources', readList(sp, 'typeSource'));
  set('minTypeConfidence', readNumber(sp, 'minTypeConfidence'));
  set('missing', readEnum(sp, 'missing', MISSING_FIELDS));
  set('readyToPublish', readBool(sp, 'readyToPublish'));
  set('minImages', readNumber(sp, 'minImages'));
  set('maxImages', readNumber(sp, 'maxImages'));
  set('minCapacity', readNumber(sp, 'minCapacity'));
  set('maxCapacity', readNumber(sp, 'maxCapacity'));
  set('capacityUnknown', readBool(sp, 'capacityUnknown'));
  set('contacts', readEnum(sp, 'contact', CONTACT_KINDS));
  set('bbox', readText(sp, 'bbox'));
  set('near', readText(sp, 'near'));
  set('radiusKm', readNumber(sp, 'radiusKm'));
  set('titleSource', readOne(sp, 'titleSource', ['partner', 'ai'] as const));
  set('aiContentFailed', readBool(sp, 'aiContentFailed'));
  set('createdFrom', readText(sp, 'createdFrom'));
  set('createdTo', readText(sp, 'createdTo'));
  set('updatedFrom', readText(sp, 'updatedFrom'));
  set('updatedTo', readText(sp, 'updatedTo'));
  set('syncedFrom', readText(sp, 'syncedFrom'));
  set('syncedTo', readText(sp, 'syncedTo'));
  set('inGroup', readBool(sp, 'inGroup'));
  set('duplicateName', readBool(sp, 'duplicateName'));
  set('addressMatchesPin', readBool(sp, 'addressMatchesPin'));
  set('hasPublished', readBool(sp, 'hasPublished'));
  set('hasOwner', readBool(sp, 'hasOwner'));
  set('hasPrice', readBool(sp, 'hasPrice'));
  set('categories', readList(sp, 'category'));
  set('tags', readList(sp, 'tag'));
  set('amenities', readList(sp, 'amenity'));
  set('sortBy', readOne(sp, 'sortBy', SORT_FIELDS));
  set('sortDir', readOne(sp, 'sortDir', ['asc', 'desc'] as const));
  const page = readNumber(sp, 'page');
  if (page !== undefined && page >= 1) set('page', Math.trunc(page));
  return p;
}

/** Whether a key holds a `||`-separated list. Exported for the API client. */
export function isPipeSeparated(key: string): boolean {
  return PIPE_SEPARATED.has(key);
}

const NOT_A_FILTER = new Set<keyof SyncedLocationListParams>(['page', 'limit', 'sortBy', 'sortDir']);

/** How many filters are set — what "Clear all (n)" counts. */
export function countActiveFilters(p: SyncedLocationListParams): number {
  let n = 0;
  for (const [key, value] of Object.entries(p) as [keyof SyncedLocationListParams, unknown][]) {
    if (NOT_A_FILTER.has(key) || value === undefined) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    n++;
  }
  return n;
}

/** The params with every filter removed, keeping only the sort. */
export function clearedFilters(p: SyncedLocationListParams): SyncedLocationListParams {
  const out: SyncedLocationListParams = {};
  if (p.sortBy !== undefined) out.sortBy = p.sortBy;
  if (p.sortDir !== undefined) out.sortDir = p.sortDir;
  return out;
}
